import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellRing, Plus, Radio, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { LiveAudienceRail } from '../components/live/LiveAudienceRail';
import { LiveLobbyCard } from '../components/live/LiveLobbyCard';
import { LiveCamerinoPanel } from '../components/live/LiveCamerinoPanel';
import { LiveSessionProfile } from '../components/live/LiveSessionProfile';
import { LiveAfterShowCard } from '../components/live/LiveAfterShowCard';
import { LiveHostBar } from '../components/live/LiveHostBar';
import { LiveTipQueue } from '../components/live/LiveTipQueue';
import { LiveTermsModal } from '../components/live/LiveTermsModal';
import { ImmersiveLiveStage } from '../components/live/ImmersiveLiveStage';
import { TicketedShowsSection, TicketedShowCard } from '../components/live/TicketedShowCard';
import { TicketPurchaseModal } from '../components/live/TicketPurchaseModal';
import { AgeVerificationModal } from '../components/live/AgeVerificationModal';
import { CreateTicketedShowForm } from '../components/live/CreateTicketedShowForm';
import { API_BASE as API } from '../lib/apiBase';


const defaultComposer = {
  title: '',
  description: '',
  cover_image: '',
  show_mode: 'performance',
  scene_preset: 'spotlight',
  tags: [],
};

export default function LivePage() {
  const { token, user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [control, setControl] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [composer, setComposer] = useState(defaultComposer);
  const [tagsInput, setTagsInput] = useState('');
  const [walletBalance, setWalletBalance] = useState(null);
  const [enteredSessionId, setEnteredSessionId] = useState(null);
  const [afterShowSession, setAfterShowSession] = useState(null);
  const [liveToken, setLiveToken] = useState('');
  const [liveUrl, setLiveUrl] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [eventAnswer, setEventAnswer] = useState('');
  const [hostPrompt, setHostPrompt] = useState('');
  const [hostCorrectAnswer, setHostCorrectAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepting, setTermsAccepting] = useState(false);
  
  // Ticketed Shows & Age Verification
  const [ticketedShows, setTicketedShows] = useState([]);
  const [userTickets, setUserTickets] = useState([]);
  const [purchaseModalShow, setPurchaseModalShow] = useState(null);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [ageVerified, setAgeVerified] = useState(true); // Will be checked on mount
  const [showCreateTicketedForm, setShowCreateTicketedForm] = useState(false);

  const authHeaders = useMemo(() => token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }, [token]);

  const currentHostSession = control?.current_session || null;
  const activeSession = selectedSession || (currentHostSession?.status === 'live' ? currentHostSession : null);
  const activeSessionId = activeSession?.id;
  const activeSessionStatus = activeSession?.status;
  const canHost = Boolean(control?.can_host);
  const needsTermsAcceptance = control?.live_terms?.needs_acceptance;
  const hostFormat = control?.recommended_format || 'room';
  const isHostForActiveSession = Boolean(token && activeSession && activeSession.host_user_id === user?.id);
  const viewerModeActive = Boolean(activeSession && (isHostForActiveSession || enteredSessionId === activeSessionId));

  /* ── Fetch ── */
  const refreshLive = async ({ keepSelection = true } = {}) => {
    setLoading(true);
    try {
      const reqs = [
        fetch(`${API}/api/live/active`).then(r => r.json()),
        fetch(`${API}/api/live/ticketed-show`).then(r => r.json()).catch(() => ({ shows: [] }))
      ];
      if (token) {
        reqs.push(fetch(`${API}/api/live/control`, { headers: authHeaders }).then(r => r.json()));
        reqs.push(fetch(`${API}/api/wallets/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null));
        reqs.push(fetch(`${API}/api/live/ticketed-show/check-age-verification`, { headers: authHeaders }).then(r => r.json()).catch(() => ({ verified: true })));
      }
      const [activeResp, ticketedResp, ctrlResp, walletResp, ageResp] = await Promise.all(reqs);
      const next = activeResp.sessions || [];
      setSessions(next);
      setTicketedShows(ticketedResp.shows || []);
      if (token && ctrlResp) {
        setControl(ctrlResp);
        if (ctrlResp.current_session) {
          setComposer({
            title: ctrlResp.current_session.title || '',
            description: ctrlResp.current_session.description || '',
            cover_image: ctrlResp.current_session.cover_image || '',
            show_mode: ctrlResp.current_session.show_mode || 'performance',
            scene_preset: ctrlResp.current_session.scene_preset || 'spotlight',
            tags: ctrlResp.current_session.tags || [],
          });
          setTagsInput((ctrlResp.current_session.tags || []).join(', '));
        }
      }
      if (walletResp) setWalletBalance(walletResp.balance ?? walletResp?.wallet?.balance ?? 0);
      if (ageResp) setAgeVerified(ageResp.verified !== false);
      if (!keepSelection) { setSelectedSession(null); }
      else if (activeSessionId && !next.find(s => s.id === activeSessionId)) { setSelectedSession(null); setEnteredSessionId(null); }
    } finally { setLoading(false); }
  };

  // Check age on first load
  useEffect(() => {
    if (token && !ageVerified) {
      setShowAgeVerification(true);
    }
  }, [token, ageVerified]);

  useEffect(() => { refreshLive({ keepSelection: false }); /* eslint-disable-next-line */ }, [token]);

  /* ── LiveKit ── */
  useEffect(() => {
    if (!activeSessionId || !token || !viewerModeActive) { setLiveToken(''); setLiveUrl(''); return; }
    let cancelled = false;
    (async () => {
      const res = await fetch(`${API}/api/live/${activeSessionId}/token`, { method: 'POST', headers: authHeaders });
      if (!res.ok) return;
      const d = await res.json();
      if (!cancelled) { setLiveToken(d.token || ''); setLiveUrl(d.livekit_url || ''); }
      if (activeSessionStatus === 'live' && !isHostForActiveSession) {
        await fetch(`${API}/api/live/${activeSessionId}/join`, { method: 'POST', headers: authHeaders });
      }
    })();
    return () => { cancelled = true; if (activeSessionStatus === 'live' && !isHostForActiveSession) fetch(`${API}/api/live/${activeSessionId}/leave`, { method: 'POST', headers: authHeaders }).catch(() => {}); };
  }, [activeSessionId, activeSessionStatus, authHeaders, isHostForActiveSession, token, viewerModeActive]);

  /* ── Poll ── */
  useEffect(() => {
    if (!activeSessionId) return undefined;
    const iv = window.setInterval(async () => {
      const r = await fetch(`${API}/api/live/${activeSessionId}`);
      if (!r.ok) return;
      const d = await r.json();
      setSelectedSession(d);
      setSessions(cur => cur.map(s => (s.id === d.id ? d : s)));
    }, 4000);
    return () => window.clearInterval(iv);
  }, [activeSessionId]);

  /* ── Handlers ── */
  const updateComposerField = (f, v) => setComposer(c => ({ ...c, [f]: v }));

  const saveStudio = async (camerinoData) => {
    setBusy(true);
    try {
      // Accept data from Camerino or use composer as fallback
      const p = camerinoData || { ...composer, tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean) };
      const url = currentHostSession ? `${API}/api/live/${currentHostSession.id}/host/settings` : `${API}/api/live/prepare`;
      const method = currentHostSession ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: authHeaders, body: JSON.stringify(p) });
      const d = await r.json();
      setControl(c => ({ ...(c || {}), current_session: d }));
      await refreshLive();
    } finally { setBusy(false); }
  };

  const goLive = async () => {
    if (!currentHostSession) return;
    
    // Check if terms need acceptance first
    if (needsTermsAcceptance) {
      setShowTermsModal(true);
      return;
    }
    
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/live/${currentHostSession.id}/go-live`, { method: 'POST', headers: authHeaders });
      const d = await r.json();
      if (!r.ok) {
        // If terms not accepted, show modal
        if (r.status === 403 && d.detail?.includes('Términos')) {
          setShowTermsModal(true);
          return;
        }
        console.error('Go live error:', d);
        return;
      }
      setSelectedSession(d); setEnteredSessionId(d.id); setCreatorOpen(false);
      setControl(c => ({ ...(c || {}), current_session: d }));
      await refreshLive();
    } finally { setBusy(false); }
  };
  
  const acceptLiveTerms = async (termsData) => {
    setTermsAccepting(true);
    try {
      const r = await fetch(`${API}/api/live/terms/accept`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(termsData)
      });
      const d = await r.json();
      if (r.ok && d.accepted) {
        // Update control to reflect terms accepted
        setControl(c => ({ ...(c || {}), live_terms: { ...c?.live_terms, accepted: true, needs_acceptance: false } }));
        setShowTermsModal(false);
        // Now proceed with go live
        goLive();
      }
    } finally { setTermsAccepting(false); }
  };

  const endLive = async () => {
    if (!activeSession) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/live/${activeSession.id}/end`, { method: 'POST', headers: authHeaders });
      const ended = await r.json();
      setAfterShowSession(ended); setCreatorOpen(false); setSelectedSession(null); setEnteredSessionId(null);
      await refreshLive({ keepSelection: false });
    } finally { setBusy(false); }
  };

  const toggleNotification = async () => {
    if (!token || !control) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/live/notifications/preference`, { method: 'POST', headers: authHeaders, body: JSON.stringify({ enabled: !control.notification_opt_in }) });
      const d = await r.json();
      setControl(c => ({ ...(c || {}), notification_opt_in: d.enabled }));
    } finally { setBusy(false); }
  };

  const sendChat = async () => {
    if (!token || !activeSession || !chatMessage.trim()) return;
    await fetch(`${API}/api/live/${activeSession.id}/chat`, { method: 'POST', headers: authHeaders, body: JSON.stringify({ message: chatMessage.trim() }) });
    setChatMessage('');
    refreshLive();
  };

  const sendReaction = async (reaction) => {
    if (!token || !activeSession) return;
    await fetch(`${API}/api/live/${activeSession.id}/react`, { method: 'POST', headers: authHeaders, body: JSON.stringify({ reaction }) });
  };

  const sendTip = async (amount, message = '') => {
    if (!token || !activeSession) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/apoyos`, { method: 'POST', headers: authHeaders, body: JSON.stringify({ live_session_id: activeSession.id, amount, message, is_live: true }) });
      if (res.ok) {
        await fetch(`${API}/api/live/${activeSession.id}/tip`, {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({ amount, message }),
        });
      }
      await refreshLive();
    } finally { setBusy(false); }
  };

  const moderateTip = async (tipId, action) => {
    if (!token || !activeSession) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/live/${activeSession.id}/host/tip/${tipId}/moderate`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ action }),
      });
      await refreshLive();
    } finally { setBusy(false); }
  };

  const launchHostEvent = async (type) => {
    if (!activeSession) return;
    const prompt = hostPrompt.trim();
    const payloads = {
      countdown: { title: 'Countdown', prompt: prompt || (lang === 'es' ? 'El pulso cambia.' : 'Pulse shift.'), duration_seconds: 20 },
      poll_yes_no: { event_type: 'poll_yes_no', title: 'Poll', prompt: prompt || (lang === 'es' ? 'Un track mas?' : 'One more track?'), options: [lang === 'es' ? 'Si' : 'Yes', 'No'], duration_seconds: 45 },
      word_drop: { event_type: 'word_drop', title: 'Word Drop', prompt: prompt || (lang === 'es' ? 'Una palabra.' : 'One word.'), duration_seconds: 60 },
      trivia_music: { event_type: 'trivia_music', title: 'Trivia', prompt: prompt || (lang === 'es' ? 'Pregunta rapida.' : 'Quick question.'), correct_answer: hostCorrectAnswer.trim(), duration_seconds: 60 },
    };
    const ep = type === 'countdown' ? `${API}/api/live/${activeSession.id}/host/countdown` : `${API}/api/live/${activeSession.id}/host/event`;
    const body = type === 'countdown' ? payloads.countdown : payloads[type];
    await fetch(ep, { method: 'POST', headers: authHeaders, body: JSON.stringify(body) });
    setHostPrompt(''); setHostCorrectAnswer('');
    refreshLive();
  };

  const clearEvent = async () => {
    if (!activeSession) return;
    await fetch(`${API}/api/live/${activeSession.id}/host/clear-event`, { method: 'POST', headers: authHeaders });
    refreshLive();
  };

  const respondToEvent = async (answer) => {
    if (!activeSession || !token || !answer?.toString().trim()) return;
    await fetch(`${API}/api/live/${activeSession.id}/event/respond`, { method: 'POST', headers: authHeaders, body: JSON.stringify({ answer: answer.toString().trim() }) });
    setEventAnswer('');
    refreshLive();
  };

  const enterSession = () => {
    if (!activeSessionId) return;
    if (!token) { navigate('/login'); return; }
    setEnteredSessionId(activeSessionId);
  };

  const openSession = (s) => { setSelectedSession(s); setEnteredSessionId(null); setAfterShowSession(null); };
  const backToMosaic = () => { setSelectedSession(null); setEnteredSessionId(null); };

  /* ── Display flags ── */
  const showStudio = creatorOpen && token && canHost;
  const showAfterShow = Boolean(afterShowSession) && !creatorOpen;
  const showStage = Boolean(activeSession && viewerModeActive);
  const showProfile = Boolean(activeSession && !viewerModeActive && !creatorOpen && !afterShowSession);
  // Show mosaic if there are regular sessions OR ticketed shows
  const showMosaic = (sessions.length > 0 || ticketedShows.length > 0) && !showStage && !showStudio && !showProfile;
  const showEmpty = !sessions.length && !ticketedShows.length && !loading && !showStudio && !showAfterShow;

  return (
    <div data-testid="pulse-live-page" className="min-h-screen bg-[#0a0a0a]">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,168,83,0.03),transparent_50%)]" />

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 pt-20 pb-32">
        {/* ── Header ── */}
        <header data-testid="pulse-live-lobby-hero" className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <h1 data-testid="pulse-live-main-title" className="text-xl font-bold text-white tracking-tight">LIVE</h1>
            {sessions.length > 0 && <span data-testid="pulse-live-active-count-card" className="text-sm text-white/25">{sessions.length}</span>}
          </div>
          <div className="flex items-center gap-2">
            {/* Artist: Create Ticketed Show */}
            {token && hostFormat === 'show' && (
              <button 
                data-testid="create-ticketed-show-btn"
                onClick={() => setShowCreateTicketedForm(true)} 
                className="rounded-full px-4 py-2.5 text-sm font-semibold flex items-center gap-2 transition border"
                style={{
                  background: 'linear-gradient(135deg, rgba(212,168,83,0.2) 0%, rgba(184,148,42,0.2) 100%)',
                  borderColor: 'rgba(212,168,83,0.4)',
                  color: '#d4a853'
                }}
              >
                <Crown size={14} />
                {lang === 'es' ? 'Show VIP' : 'VIP Show'}
              </button>
            )}
            {token && (
              <button data-testid="pulse-live-notification-button" onClick={toggleNotification} disabled={busy} className="rounded-full bg-white/5 border border-white/10 p-2.5 text-white/40 hover:text-white disabled:opacity-30 transition">
                <BellRing size={16} />
              </button>
            )}
            <button data-testid="pulse-live-primary-creator-button" onClick={() => token ? setCreatorOpen(true) : navigate('/login')} className="rounded-full bg-white text-[#0a0a0a] px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-white/90 transition">
              <Plus size={14} />
              {token ? (hostFormat === 'show' ? 'Show' : 'Room') : (lang === 'es' ? 'Entrar' : 'Sign in')}
            </button>
          </div>
        </header>

        {/* ── Camerino (Studio) ── */}
        {showStudio && (
          <LiveCamerinoPanel
            session={currentHostSession}
            onSave={saveStudio}
            onGoLive={goLive}
            onBack={() => setCreatorOpen(false)}
            lang={lang}
            isArtist={control?.host_profile?.role === 'artist'}
            busy={busy}
          />
        )}

        {/* ── After show ── */}
        {showAfterShow && (
          <LiveAfterShowCard session={afterShowSession} lang={lang} onReturn={() => setAfterShowSession(null)} onReplay={() => { setAfterShowSession(null); setCreatorOpen(true); }} />
        )}

        {/* ── Pre-entry ── */}
        {showProfile && (
          <LiveSessionProfile session={activeSession} lang={lang} token={token} notificationEnabled={Boolean(control?.notification_opt_in)} onEnter={enterSession} onToggleNotification={toggleNotification} onAuth={() => navigate('/login')} onBack={backToMosaic} />
        )}

        {/* ── Empty state ── */}
        {showEmpty && (
          <section data-testid="pulse-live-pause-state" className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="relative w-20 h-20 mb-8">
              <div className="absolute inset-0 rounded-full bg-red-500/8 animate-ping" style={{ animationDuration: '3s' }} />
              <div className="absolute inset-3 rounded-full bg-red-500/12 animate-pulse" />
              <div className="absolute inset-0 flex items-center justify-center"><Radio size={28} className="text-red-400/70" /></div>
            </div>
            <p className="text-white/20 text-sm mb-6">{lang === 'es' ? 'Nadie en vivo' : 'No one live'}</p>
            <button data-testid="pulse-live-break-silence" onClick={() => token ? setCreatorOpen(true) : navigate('/login')} className="rounded-full bg-white text-[#0a0a0a] px-6 py-2.5 text-sm font-semibold hover:bg-white/90 transition">
              {token ? (lang === 'es' ? 'Se el primero' : 'Be the first') : (lang === 'es' ? 'Entrar' : 'Sign in')}
            </button>
            {token && (
              <button data-testid="pulse-live-notify-toggle" onClick={toggleNotification} disabled={busy} className="mt-3 text-[12px] text-white/20 hover:text-white/40 transition disabled:opacity-30">
                {control?.notification_opt_in ? (lang === 'es' ? 'Senal activada' : 'Signal on') : (lang === 'es' ? 'Activar senal' : 'Enable signal')}
              </button>
            )}
          </section>
        )}

        {/* ── Mosaic ── */}
        {showMosaic && (
          <section data-testid="pulse-live-spotlight-section" className="mt-2">
            {/* Ticketed Shows Section */}
            <TicketedShowsSection
              shows={ticketedShows}
              onPurchase={(show) => setPurchaseModalShow(show)}
              onView={(show) => openSession(show)}
              userTickets={userTickets}
              lang={lang}
            />
            
            {/* Regular Live Sessions */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {sessions.map((s, i) => {
                const feat = s.session_format === 'show' || s.presence_tier === 'diamond' || s.presence_tier === 'spotlight';
                return (
                  <div key={s.id} className={feat && i < 2 ? 'col-span-2 row-span-2' : ''}>
                    <LiveLobbyCard session={s} lang={lang} onOpen={openSession} featured={feat && i < 2} index={i} />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {loading && !sessions.length && (
          <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-red-500 animate-spin" /></div>
        )}
      </div>
      
      {/* ══════════════════════════════════════════════════════════════════════
         IMMERSIVE STAGE — Fullscreen cinematic experience (rendered outside container)
      ══════════════════════════════════════════════════════════════════════ */}
      {showStage && (
        <ImmersiveLiveStage
          session={activeSession}
          token={token}
          liveToken={liveToken}
          liveUrl={liveUrl}
          isHost={isHostForActiveSession}
          lang={lang}
          walletBalance={walletBalance}
          onSendChat={sendChat}
          onSendTip={sendTip}
          onReact={sendReaction}
          onRespondEvent={respondToEvent}
          onClearEvent={clearEvent}
          onEndLive={endLive}
          onBack={backToMosaic}
        />
      )}
      
      {/* Host controls overlay (for tip moderation when in immersive mode) */}
      {showStage && isHostForActiveSession && (
        <div className="fixed bottom-4 right-4 z-50 w-80">
          <LiveTipQueue session={activeSession} lang={lang} onModerate={moderateTip} busy={busy} />
        </div>
      )}
      
      {/* Live Terms Modal */}
      <LiveTermsModal 
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={acceptLiveTerms}
        lang={lang}
        isLoading={termsAccepting}
      />
      
      {/* Age Verification Modal (+18) */}
      {showAgeVerification && (
        <AgeVerificationModal
          token={token}
          lang={lang}
          onVerified={() => {
            setAgeVerified(true);
            setShowAgeVerification(false);
          }}
          onCancel={() => navigate('/')}
        />
      )}
      
      {/* Ticket Purchase Modal */}
      {purchaseModalShow && (
        <TicketPurchaseModal
          show={purchaseModalShow}
          walletBalance={walletBalance}
          token={token}
          lang={lang}
          onClose={() => setPurchaseModalShow(null)}
          onSuccess={(ticket) => {
            setUserTickets(prev => [...prev, ticket]);
            setPurchaseModalShow(null);
            refreshLive();
          }}
        />
      )}
      
      {/* Create Ticketed Show Modal (for artists) */}
      {showCreateTicketedForm && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
          <div className="w-full max-w-lg">
            <CreateTicketedShowForm
              token={token}
              lang={lang}
              onSuccess={() => {
                setShowCreateTicketedForm(false);
                refreshLive();
              }}
              onCancel={() => setShowCreateTicketedForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
